/**
 * useInterruptedSessions Hook
 * Manages interrupted batch recordings stored in IndexedDB.
 *
 * Provides:
 * - Load interrupted recordings on mount
 * - Resume a recording (restore chunks to CreateContext)
 * - Discard a recording (delete from IndexedDB)
 * - Preview a recording (play audio from IndexedDB)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { indexedDBSessionService, type InterruptedRecording } from '@/services/indexedDBSessionService';
import { createLogger } from '@/lib/logger';

const log = createLogger('useInterruptedSessions');

export interface InterruptedRecordingInfo {
  sessionId: string;
  duration: number;
  languageCode: string;
  startedAt: number;
  lastSavedAt: number;
  minutesAgo: number;
}

interface UseInterruptedSessionsReturn {
  interruptedRecordings: InterruptedRecordingInfo[];
  isLoading: boolean;
  resumeRecording: (sessionId: string) => Promise<InterruptedRecording | null>;
  discardRecording: (sessionId: string) => Promise<void>;
  refreshRecordings: () => Promise<void>;
  // Preview
  previewSessionId: string | null;
  previewBlobUrl: string | null;
  previewLoading: boolean;
  previewRecording: (sessionId: string) => Promise<void>;
  stopPreview: () => void;
}

export function useInterruptedSessions(): UseInterruptedSessionsReturn {
  const [interruptedRecordings, setInterruptedRecordings] = useState<InterruptedRecordingInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Preview state
  const [previewSessionId, setPreviewSessionId] = useState<string | null>(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const blobUrlRef = useRef<string | null>(null);

  /**
   * Revoke current blob URL to free memory
   */
  const revokeBlobUrl = useCallback(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, []);

  /**
   * Stop audio preview and clean up
   */
  const stopPreview = useCallback(() => {
    revokeBlobUrl();
    setPreviewSessionId(null);
    setPreviewBlobUrl(null);
    setPreviewLoading(false);
  }, [revokeBlobUrl]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
    };
  }, []);

  /**
   * Load interrupted recordings from IndexedDB
   */
  const loadRecordings = useCallback(async () => {
    try {
      setIsLoading(true);
      const recordings = await indexedDBSessionService.getAllRecordings();

      // Map to display-friendly format
      const now = Date.now();
      const recordingsInfo: InterruptedRecordingInfo[] = recordings.map(r => ({
        sessionId: r.sessionId,
        duration: r.duration,
        languageCode: r.languageCode,
        startedAt: r.startedAt,
        lastSavedAt: r.lastSavedAt,
        minutesAgo: Math.round((now - r.lastSavedAt) / 60000),
      }));

      // Sort by most recent first
      recordingsInfo.sort((a, b) => b.lastSavedAt - a.lastSavedAt);

      setInterruptedRecordings(recordingsInfo);
      log.info('Loaded interrupted recordings', { count: recordingsInfo.length });
    } catch (err) {
      log.error('Failed to load interrupted recordings', err as Error);
      setInterruptedRecordings([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Resume a recording - retrieves full recording data from IndexedDB
   */
  const resumeRecording = useCallback(async (sessionId: string): Promise<InterruptedRecording | null> => {
    try {
      log.info('Resuming recording', { sessionId });
      const recording = await indexedDBSessionService.getRecording(sessionId);

      if (!recording) {
        log.warn('Recording not found in IndexedDB', { sessionId });
        // Refresh list to remove stale entry
        await loadRecordings();
        return null;
      }

      log.info('Recording retrieved for resume', {
        sessionId,
        chunks: recording.chunks.length,
        duration: recording.duration
      });

      return recording;
    } catch (err) {
      log.error('Failed to resume recording', err as Error);
      return null;
    }
  }, [loadRecordings]);

  /**
   * Discard a recording - deletes from IndexedDB
   */
  const discardRecording = useCallback(async (sessionId: string): Promise<void> => {
    try {
      log.info('Discarding recording', { sessionId });

      // Stop preview if discarding the previewed recording
      if (previewSessionId === sessionId) {
        stopPreview();
      }

      await indexedDBSessionService.deleteRecording(sessionId);

      // Remove from local state
      setInterruptedRecordings(prev => prev.filter(r => r.sessionId !== sessionId));

      log.info('Recording discarded', { sessionId });
    } catch (err) {
      log.error('Failed to discard recording', err as Error);
      throw err;
    }
  }, [previewSessionId, stopPreview]);

  /**
   * Preview a recording - loads audio from IndexedDB and creates a blob URL
   */
  const previewRecording = useCallback(async (sessionId: string): Promise<void> => {
    // If already previewing this session, stop it
    if (previewSessionId === sessionId) {
      stopPreview();
      return;
    }

    try {
      // Clean up any existing preview
      revokeBlobUrl();
      setPreviewSessionId(sessionId);
      setPreviewLoading(true);
      setPreviewBlobUrl(null);

      log.info('Loading preview', { sessionId });
      const recording = await indexedDBSessionService.getRecording(sessionId);

      if (!recording) {
        log.warn('Recording not found for preview', { sessionId });
        stopPreview();
        return;
      }

      const blob = new Blob(recording.chunks, { type: recording.mimeType });
      const url = URL.createObjectURL(blob);
      blobUrlRef.current = url;

      setPreviewBlobUrl(url);
      setPreviewLoading(false);

      log.info('Preview ready', { sessionId, mimeType: recording.mimeType, chunks: recording.chunks.length });
    } catch (err) {
      log.error('Failed to load preview', err as Error);
      stopPreview();
    }
  }, [previewSessionId, stopPreview, revokeBlobUrl]);

  /**
   * Manually refresh the recordings list
   */
  const refreshRecordings = useCallback(async () => {
    await loadRecordings();
  }, [loadRecordings]);

  // Load recordings on mount
  useEffect(() => {
    loadRecordings();
  }, [loadRecordings]);

  return {
    interruptedRecordings,
    isLoading,
    resumeRecording,
    discardRecording,
    refreshRecordings,
    previewSessionId,
    previewBlobUrl,
    previewLoading,
    previewRecording,
    stopPreview,
  };
}
